import React from 'react';

import { ChevronDown } from '@depop/web-thrift-ui/Icons/ChevronDown';
import { Heading } from '@depop/web-thrift-ui/Typography';
import { ArrowDownIcon } from '@depop/web-ui-kit/Icons/ArrowDownIcon';
import { ArrowTopIcon } from '@depop/web-ui-kit/Icons/ArrowTopIcon';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

type Props = {
  children: React.ReactNode;
  totalPrice: string;
  /**
   * 'default': content unmounts while closed; parent handles breakpoints.
   * 'responsive': content stays mounted — collapsible below lg, forced open at lg+.
   */
  variant?: 'default' | 'responsive';
};

export function OrderSummaryAccordionWrapper({
  children,
  totalPrice,
  variant = 'default',
}: Props) {
  const [isOpen, setIsOpen] = React.useState(false);
  const t = useTranslations('checkout');
  const isResponsive = variant === 'responsive';

  function handleToggle() {
    setIsOpen((prev) => !prev);
  }

  const defaultModeIcon = isOpen ? (
    <ArrowTopIcon size={24} />
  ) : (
    <ArrowDownIcon size={24} />
  );

  return (
    <>
      {isResponsive && (
        <Heading variant="md" as="h2" className={styles.accordionDesktopHeader}>
          {t('OrderSummaryTitle')}
        </Heading>
      )}
      <button
        className={clsx(styles.accordionToggleButton, {
          [styles['accordionToggleButton--responsive']]: isResponsive,
        })}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls="orderSummaryAccordionContent"
      >
        <div className={styles.accordionTitle}>
          {isResponsive ? (
            <Heading variant="md" as="h2">
              {t('OrderSummaryTitle')}
            </Heading>
          ) : (
            <Heading variant="md">{t('OrderSummaryTitle')}</Heading>
          )}
          {isResponsive ? (
            <span className={styles.accordionChevron} aria-hidden>
              <ChevronDown border="regular" size="sm" />
            </span>
          ) : (
            defaultModeIcon
          )}
        </div>

        <Heading variant="sm" as="p">
          {totalPrice}
        </Heading>
      </button>

      {isResponsive ? (
        <div
          id="orderSummaryAccordionContent"
          className={clsx(styles.accordionResponsiveContent, {
            [styles['accordionResponsiveContent--expanded']]: isOpen,
          })}
        >
          {children}
        </div>
      ) : (
        isOpen && (
          <div
            id="orderSummaryAccordionContent"
            className={styles.accordionContent}
          >
            {children}
          </div>
        )
      )}
    </>
  );
}
